import type { ReactNode } from "react";
import BaseDialog from "../Modals/BaseDialog";

export function ConfirmationDialog({
  title,
  description,
  extra,
  onConfirm,
  confirmText,
  confirmVariant,
  confirmDisabled = false,
  cancelText,
  isOpen,
  onOpenChange,
  isLoading = false,
  children,
  onCancel,
  cancelVariant = "ghost",
  size,
}: {
  title: ReactNode;
  description: ReactNode;
  // Rendered below the message, for anything the confirmation needs to add
  // (e.g. the analytics/billing notice on a delete).
  extra?: ReactNode;
  onConfirm: () => void;
  confirmText?: string;
  confirmVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  confirmDisabled?: boolean;
  cancelText?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  /** Extra content below the description — e.g. a caveat in a NoxusAlert. */
  children?: React.ReactNode;
  // Runs instead of closing when Cancel is clicked, for callers that undo a pending change.
  onCancel?: () => void;
  cancelVariant?: "ghost" | "outline";
  size?: React.ComponentProps<typeof BaseDialog>["size"];
}) {
  return (
    <BaseDialog
      compact
      size={size}
      title={title}
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (isLoading && !open) return;
        onOpenChange(open);
      }}
      footerActions={[
        {
          label: cancelText || "Cancel",
          props: {
            variant: cancelVariant,
            onClick: onCancel ?? (() => onOpenChange(false)),
            disabled: isLoading,
          },
        },
        {
          label: confirmText || "Confirm",
          props: {
            variant: confirmVariant,
            onClick: onConfirm,
            loading: isLoading,
            disabled: confirmDisabled,
          },
        },
      ]}
    >
      <div className="flex flex-col gap-3">
        {description && (
          <p className="text-sm text-foreground">{description}</p>
        )}
        {extra}
        {children}
      </div>
    </BaseDialog>
  );
}
